import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { TagFlowViewState, TagResultState } from '@ui/Utils/scan/tag/scanTypes';

import { useTagScanFlow } from 'kerberus-components/src/Utils/scan/tag/useTagScanFlow';

import NoOwnerView from './NoOwnerView';
import TagNotAddedView from './TagNotAddedView';
import TagOwnerView from './TagOwnerView';

interface TagFlowResultContentProps {
    data: TagResultState;
    onInfo: () => void;
    onOpenLink: () => void;
    onScanAgain: () => void;
}

export default function TagFlowResultContent({
    data,
    onInfo,
    onOpenLink,
    onScanAgain,
}: Readonly<TagFlowResultContentProps>) {
    const { mutateOwnerPermission, mutateTagExists, serial = '', viewState } = useTagScanFlow(data);

    const [countdown, setCountdown] = useState<number | null>(null);

    const onRefresh = useCallback(() => {
        if (countdown !== null) {
            return;
        }
        setCountdown(5);
        void mutateTagExists();
        void mutateOwnerPermission();
    }, [countdown, mutateTagExists, mutateOwnerPermission]);

    useEffect(() => {
        if (countdown === null || countdown <= 0) {
            return;
        }
        const timer = setTimeout(() => {
            setCountdown((prev) => (prev !== null && prev > 1 ? prev - 1 : null));
        }, 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // viewState = TagFlowViewState.EmptyTag; // TEMP OVERRIDE FOR TESTING

    // States listed below:
    // Unknown / Loading (Unknown)
    // Not a Renki tag (NotRenki)
    // Not added (NotAdded)
    // Compromised (Compromised)
    // Navigate (Navigate)
    // Tag owner (TagOwner)
    // No Owner (NoOwner)
    // Wrong Domain (WrongDomain)

    const renderFlowSpecific = () => {
        switch (viewState) {
            case TagFlowViewState.Unknown:
                return (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress />
                        <Stack spacing={0.5} sx={{ textAlign: 'center' }}>
                            <Typography variant="h5">Loading...</Typography>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                }}
                                variant="body2"
                            >
                                If this takes too long, please try scanning again.
                            </Typography>
                        </Stack>
                        <Button
                            color="inherit"
                            onClick={onScanAgain}
                            size="large"
                            startIcon={<ReplayIcon fontSize="inherit" />}
                            variant="outlined"
                        >
                            Scan Again
                        </Button>
                    </Stack>
                );
            case TagFlowViewState.NotRenki:
                return (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <ErrorOutlineRoundedIcon sx={{ color: 'warning.main', fontSize: 56 }} />
                        <Typography color="warning" variant="body2">
                            This tag is not a Renki tag.
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                color="warning"
                                onClick={onInfo}
                                size="large"
                                startIcon={<InfoOutlinedIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Info
                            </Button>
                            <Button
                                color="inherit"
                                onClick={onScanAgain}
                                size="large"
                                startIcon={<ReplayIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Scan Again
                            </Button>
                        </Stack>
                    </Stack>
                );
            case TagFlowViewState.NotAdded:
                return <TagNotAddedView onScanAgain={onScanAgain} serial={serial} />;
            case TagFlowViewState.Compromised:
                return (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <ErrorOutlineRoundedIcon sx={{ color: 'error.main', fontSize: 56 }} />
                        <Typography color="error" variant="body2">
                            This Renki tag is compromised. Do not open this link.
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                color="error"
                                onClick={onInfo}
                                size="large"
                                startIcon={<InfoOutlinedIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Info
                            </Button>
                            <Button
                                color="inherit"
                                onClick={onScanAgain}
                                size="large"
                                startIcon={<ReplayIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Scan Again
                            </Button>
                        </Stack>
                    </Stack>
                );
            case TagFlowViewState.Navigate:
                return (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <CheckCircleRoundedIcon sx={{ color: 'success.main', fontSize: 56 }} />
                        <Typography color="success" variant="body2">
                            This tag is valid. You can open the linked resource.
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                color="primary"
                                onClick={onOpenLink}
                                size="large"
                                startIcon={<LaunchIcon fontSize="inherit" />}
                                variant="contained"
                            >
                                Open Link
                            </Button>
                            <Button
                                color="inherit"
                                onClick={onScanAgain}
                                size="large"
                                startIcon={<ReplayIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Scan Again
                            </Button>
                        </Stack>
                    </Stack>
                );
            case TagFlowViewState.TagOwner:
                return <TagOwnerView onOpenLink={onOpenLink} onScanAgain={onScanAgain} serial={serial} />;

            case TagFlowViewState.NoOwner:
                return <NoOwnerView onScanAgain={onScanAgain} serial={serial} />;
            case TagFlowViewState.WrongDomain:
                return (
                    <Stack
                        direction="column"
                        spacing={2}
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                        }}
                    >
                        <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 56 }} />
                        <Typography color="info" variant="body2">
                            This tag is from a different environment.
                        </Typography>
                        <Stack spacing={2}>
                            <Button
                                color="info"
                                onClick={onInfo}
                                size="large"
                                startIcon={<InfoOutlinedIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Info
                            </Button>
                            <Button
                                color="inherit"
                                onClick={onScanAgain}
                                size="large"
                                startIcon={<ReplayIcon fontSize="inherit" />}
                                variant="outlined"
                            >
                                Scan Again
                            </Button>
                        </Stack>
                    </Stack>
                );
            default:
                return null;
        }
    };

    return (
        <Stack
            spacing={3}
            sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                position: 'relative',
            }}
        >
            {Boolean(data) && (
                <Box
                    sx={{
                        alignItems: 'center',
                        bottom: 10,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'absolute',
                        right: 15,
                    }}
                >
                    <IconButton
                        disabled={countdown !== null}
                        onClick={onRefresh}
                        sx={{
                            border: '2px solid',
                            borderColor: countdown ? 'disabled.text' : 'primary.main',
                            borderRadius: '50%',
                            height: 48,
                            width: 48,
                        }}
                    >
                        {countdown !== null ? (
                            <Typography sx={{ fontSize: '1rem' }} variant="body2">
                                {countdown}
                            </Typography>
                        ) : (
                            <RefreshIcon color="primary" />
                        )}
                    </IconButton>
                    <Typography
                        sx={{
                            color: countdown ? 'disabled.text' : 'primary.main',
                            fontSize: '0.75rem',
                            fontWeight: countdown ? 'normal' : 'medium',
                            mt: 0.5,
                        }}
                        variant="body2"
                    >
                        Refresh
                    </Typography>
                </Box>
            )}
            {/* <Typography variant="h6">Flow State: {viewState}</Typography> */}
            <Stack
                spacing={2}
                sx={{
                    alignItems: 'center',
                }}
            >
                {renderFlowSpecific()}
            </Stack>
        </Stack>
    );
}
